/**
 * Notification Bell Component
 * Displays notification count and dropdown with notifications
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItemButton,
  Chip,
  CircularProgress,
  Backdrop
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive,
  ClearAll,
  Circle
} from '@mui/icons-material';

import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../auth/contexts/AuthContext';
import demandService from '../services/demandService';
import { formatDateTime } from '../../../shared/utils/formatters';

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const notificationAudio = new Audio('/sound/notification-sound.mp3');
  notificationAudio.preload = 'auto';
  notificationAudio.volume = 1.0;
  const prevCountRef = useRef(0);
  const navigationInProgressRef = useRef(false);
  const navigationTimerRef = useRef(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (user?.id) {
      loadNotificationCount();

      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        loadNotificationCount();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [user?.id]);

  useEffect(() => {
    const handleFocus = () => {
      if (user?.id) {
        loadNotificationCount();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.id]);

  // Ensure transient notification UI is reset after any route navigation.
  useEffect(() => {
    navigationInProgressRef.current = false;
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }
    setIsNavigating(false);
    setShowSpinner(false);
    setAnchorEl(null);
  }, [location.key, location.pathname, location.search]);

  useEffect(() => {
    return () => {
      if (navigationTimerRef.current) {
        clearTimeout(navigationTimerRef.current);
      }
    };
  }, []);

  const startNavigationState = () => {
    navigationInProgressRef.current = true;
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
    }
    navigationTimerRef.current = setTimeout(() => {
      navigationInProgressRef.current = false;
      setIsNavigating(false);
      setShowSpinner(false);
      navigationTimerRef.current = null;
    }, 15000);
    setIsNavigating(true);
    setShowSpinner(location.pathname?.toLowerCase().includes('/candidate-pipeline'));
  };

  const stopNavigationState = () => {
    navigationInProgressRef.current = false;
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }
    setIsNavigating(false);
    setShowSpinner(false);
  };

  // Show desktop notification (appears over all applications)
  const showDesktopNotification = async (title, body, icon = '/favicon.ico') => {
    // Check permission first
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission !== 'granted') {
      return false;
    }

    try {
      const notification = new Notification(title, {
        body: body,
        icon: icon,
        badge: icon,
        tag: 'recruitment-notification-' + Date.now(), // Unique tag to prevent grouping
        requireInteraction: false,
        silent: false,
        timestamp: Date.now(),
        renotify: true
      });

      // Auto-close notification after 8 seconds
      setTimeout(() => {
        notification.close();
      }, 8000);

      // Handle notification click - focus the window and navigate to specific demand
      notification.onclick = () => {
        window.focus();
        const demandCode = extractDemandCode(body);
        navigateToDemand(demandCode);
        notification.close();
      };

      return true;
    } catch (error) {
      return false;
    }
  };

  // Extract demand code from notification message
  const extractDemandCode = (message) => {
    if (!message) return null;

    // Look for various demand code patterns
    const patterns = [
      /\b[A-Z]{2}_\d{2}_\d{2}_\d{3,4}\b/g,  // DC_25_12_004
      /\b[A-Z]{2}\d{8,10}\b/g,               // DC25120004
      /\bDC[-_]?\d{2}[-_]?\d{2}[-_]?\d{3,4}\b/gi, // DC-25-12-004, dc_25_12_004
      /\b[A-Z]{2,3}[-_]?\d{2,4}[-_]?\d{2,4}[-_]?\d{2,4}\b/g // More flexible pattern
    ];

    for (const pattern of patterns) {
      const matches = message.match(pattern);
      if (matches && matches.length > 0) {
        return matches[0];
      }
    }

    return null;
  };

  // Navigate to specific demand based on demand code
  const navigateToDemand = async (demandCode) => {
    if (navigationInProgressRef.current) return;
    startNavigationState();
    if (!demandCode) {
      navigate('/demands');
      stopNavigationState();
      return;
    }

    try {

      // Get demand details by code to navigate to candidate pipeline
      const demandsResponse = await demandService.getDemandList(
        user?.id,
        user?.roleName || '',
        user?.companyID || 1
      );

      // Handle API response structure - API returns { dList: [...] }
      let demands = [];
      if (Array.isArray(demandsResponse)) {
        demands = demandsResponse;
      } else if (demandsResponse && demandsResponse.dList && Array.isArray(demandsResponse.dList)) {
        demands = demandsResponse.dList;
      } else if (demandsResponse && demandsResponse.data && Array.isArray(demandsResponse.data)) {
        demands = demandsResponse.data;
      } else if (demandsResponse && Array.isArray(demandsResponse.demands)) {
        demands = demandsResponse.demands;
      }

      const demand = demands.find(d => {
        const currentCode = d.demandCode || d.Dcode || d.DemandCode;
        return currentCode === demandCode;
      });

      if (demand) {
        // Use the exact same navigation structure as DemandList
        const navigationState = {
          DemandReqID: demand.demandRequestID || demand.demandReqID,
          Dcode: demand.demandCode || demand.Dcode,
          client: demand.client || demand.clientName,
          skill: demand.skillSet,
          exp: demand.experience,
          Step: demand.step,
          NextStepName: demand.nextStepName,
          isview: 1, // Set to 1 (not SPOC view)
          activeRoleName: user?.roleName || '',
          activeTab: user?.roleName || '',
          jobDescription: demand.jobDescription || demand.JobDescription || '',
          location: demand.location || demand.Location || demand.locationName || '',
          budgetMin: demand.BudgetMin || demand.budgetMin || '',
          budgetMax: demand.BudgetMax || demand.budgetMax || '',
          // Client additional info
          clientDemandID: demand.clientDemandID || demand.ClientDemandID || '',
          clientSPOCID: demand.clientSPOC || demand.ClientSPOCID || '',
          // Screen assignments
          screen1EmployeeId: demand.screen1EmployeeId || demand.Screen1EmployeeId,
          screen2EmployeeId: demand.screen2EmployeeId || demand.Screen2EmployeeId,
          screen3EmployeeId: demand.screen3EmployeeId || demand.Screen3EmployeeId,
          stepStr: demand.stepStr || demand.StepStr || demand.stepstr || '',
          // Recruiter assignment - MISSING FIX
          recruiterID: demand.recruiterID || demand.RecruiterID || '',
        };

        // Navigate to candidate pipeline with demand details
        navigate('/candidate-pipeline', { state: navigationState });
        enqueueSnackbar(`Navigating to demand ${demandCode}`, { variant: 'success' });
      } else {
        // If demand not found, go to demands list
        enqueueSnackbar(`Demand ${demandCode} not found. Redirecting to demands list.`, { variant: 'warning' });
        navigate('/demands');
      }
    } catch (error) {
      enqueueSnackbar('Failed to navigate to demand. Redirecting to demands list.', { variant: 'error' });
      navigate('/demands');
    } finally {
      stopNavigationState();
    }
  };

  // const loadNotificationCount = async () => {
  //   try {
  //     const count = await demandService.getNotificationCount(user?.id);
  //     logger.info('Notification count received:', count, 'for user:', user?.id);
  //     const finalCount = typeof count === 'number' ? count : 0;
  //     setNotificationCount(finalCount);
  //     logger.info('Notification count set to:', finalCount);
  //   } catch (error) {
  //     logger.error('Error loading notification count:', error);
  //   }
  // };

  const loadNotificationCount = async () => {
    try {
      const count = await demandService.getNotificationCount(user?.id);
      const finalCount = typeof count === 'number' ? count : 0;

      if (finalCount > prevCountRef.current) {
        // New notification detected
        const newNotificationCount = finalCount - prevCountRef.current;

        // Play audio notification
        notificationAudio.currentTime = 0;
        notificationAudio.play().catch(() => { });

        // Try to get the latest notification content
        try {
          const notificationData = await demandService.getNotifications(user?.id);
          let latestNotificationMessage = null;

          if (notificationData && (notificationData.nlist || notificationData.Nlist)) {
            const notifList = notificationData.nlist || notificationData.Nlist;
            if (Array.isArray(notifList) && notifList.length > 0) {
              // Get the most recent notification
              const latestNotification = notifList[0];
              latestNotificationMessage = latestNotification.notification || latestNotification.Notification;
            }
          }

          // Show ONLY desktop notification (over all apps)
          const title = newNotificationCount === 1 ? "New Notification" : "New Notifications";
          const body = latestNotificationMessage ||
            (newNotificationCount === 1 ? "You have a new notification" : `You have ${newNotificationCount} new notifications`);

          // Show desktop notification (appears over all applications)
          const desktopShown = await showDesktopNotification(title, body);

          if (!desktopShown && Notification.permission !== 'granted') {
            // Permission not granted, could show in-app notification instead
          }

        } catch (error) {
          // Fallback to generic message if we can't get notification details
          const title = newNotificationCount === 1 ? "New Notification" : "New Notifications";
          const body = newNotificationCount === 1 ? "You have a new notification" : `You have ${newNotificationCount} new notifications`;

          showDesktopNotification(title, body);
        }

      }

      prevCountRef.current = finalCount;
      setNotificationCount(finalCount);
    } catch (error) {
      // Error loading notification count
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await demandService.getNotifications(user?.id);

      // The API returns an object with nlist property (lowercase)
      if (data && (data.nlist || data.Nlist)) {
        const notifList = data.nlist || data.Nlist;
        setNotifications(Array.isArray(notifList) ? notifList : []);
      } else if (Array.isArray(data)) {
        setNotifications(data);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      enqueueSnackbar('Failed to load notifications', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    if (navigationInProgressRef.current) return;

    // Close menu immediately so it does not stay open during/after navigation.
    handleClose();

    // Mark as read if not already read
    if (notification.isRead === 0 || notification.isread === 0) {
      try {
        await demandService.markAsRead(notification.notificationId || notification.NotificationID);
        // Update local state to reflect read status
        setNotifications(prev => prev.map(n =>
          (n.notificationId === notification.notificationId || n.NotificationID === notification.NotificationID)
            ? { ...n, isRead: 1, isread: 1 }
            : n
        ));
        setNotificationCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read', error);
      }
    }

    // Extract demand code from notification message and navigate to specific demand
    const message = notification.notification || notification.Notification;
    const demandCode = extractDemandCode(message);

    if (demandCode) {
      await navigateToDemand(demandCode);
    } else {
      startNavigationState();
      navigate('/demands');
      stopNavigationState();
    }
  };



  const handleMarkAllAsRead = async () => {
    try {
      await demandService.clearNotificationCount(user?.id);
      setNotificationCount(0);
      // Also reload the notification count from backend to ensure sync
      setTimeout(() => {
        loadNotificationCount();
      }, 500);
      enqueueSnackbar('Marked all as read', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to mark as read', { variant: 'error' });
    }
  };

  const handleClearAll = async () => {
    try {
      // First mark all as read, then clear all notifications
      await demandService.clearNotificationCount(user?.id);

      await demandService.clearAllNotifications(user?.id);

      // Immediately update local state
      setNotifications([]);
      setNotificationCount(0);

      // Force reload from backend after a delay to ensure sync
      setTimeout(async () => {
        await loadNotificationCount();
      }, 1500); // Increased delay to 1.5 seconds

      enqueueSnackbar('All notifications cleared', { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar('Failed to clear notifications', { variant: 'error' });
      // Reload count even on error to ensure UI is in sync
      setTimeout(() => {
        loadNotificationCount();
      }, 1000);
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label={`${notificationCount} notifications`}
        size="large"
      >
        <Badge badgeContent={notificationCount} color="error">
          {notificationCount > 0 ? (
            <NotificationsActive />
          ) : (
            <NotificationsIcon />
          )}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        keepMounted
        disableAutoFocusItem
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              width: 460,
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 520,
              overflow: 'hidden'
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Notifications
            {notificationCount > 0 && (
              <Chip
                label={notificationCount}
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {notificationCount > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllAsRead}
                startIcon={<ClearAll />}
              >
                Mark as Read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                size="small"
                color="error"
                onClick={handleClearAll}
              >
                Clear All
              </Button>
            )}
          </Box>
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ maxHeight: 430, overflow: 'auto', py: 0 }}>
              {notifications.map((notification) => (
                <React.Fragment key={notification.notificationId || notification.NotificationID}>
                  <ListItemButton
                    disabled={isNavigating}
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      {(notification.isRead === 0 || notification.isread === 0) && (
                        <Circle sx={{ fontSize: 8, color: 'primary.main', mt: 1 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: (notification.isRead === 0 || notification.isread === 0) ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            cursor: 'pointer'
                          }}
                          title={notification.notification || notification.Notification} // Use native HTML title for long text
                        >
                          {(() => {
                            const message = notification.notification || notification.Notification;
                            const demandCode = extractDemandCode(message);

                            if (demandCode && message) {
                              // Use regex to find and replace the demand code with highlighted version
                              const regex = new RegExp(`(${demandCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                              const parts = message.split(regex);

                              return parts.map((part, index) => {
                                // Check if this part matches the demand code (case insensitive)
                                if (part.toLowerCase() === demandCode.toLowerCase()) {
                                  return (
                                    <Box
                                      key={index}
                                      component="span"
                                      sx={{
                                        color: 'primary.main',
                                        fontWeight: 700,
                                        textDecoration: 'underline',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      {part}
                                    </Box>
                                  );
                                }
                                return part;
                              });
                            }
                            return message;
                          })()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(notification.notificationDateAndTime || notification.NotificationTime)}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
            </List>


          </>
        )}
      </Menu>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={showSpinner}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2">Opening demand...</Typography>
        </Box>
      </Backdrop>
    </>
  );
};

export default NotificationBell;

